import { Menu as MenuIcon } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material";
import {
  AppBar,
  Box,
  CardMedia,
  Container,
  Divider,
  Drawer,
  drawerClasses,
  IconButton,
  styled,
  Toolbar,
} from "@mui/material";
import { useState } from "react";
import type { MenuContext } from "../../context/MenuContext";
import { MenuContextProvider } from "../../context/MenuContext";
import { Permissions } from "../../types";
import type { MenuItemsType } from "./listItems";
import { MenuItems } from "./MenuItemsComponent";

export interface Props {
  children: JSX.Element;
  menuItems: MenuItemsType;
  permissions: Permissions | null;
  logo: string;
  additionalContent?: JSX.Element;
}
type Component = (props: Props) => JSX.Element;

const drawerWidth = 240;
const smallDrawerWidth = 0;

const Div = styled("div")({});
// as of writing, there seems to be a bug with the toolbar mixin when used with sx
// apply it using the styled API instead
const ToolbarDiv = styled("div")(({ theme }) => theme.mixins.toolbar);
const Main = styled("main")({});

const rootStyles: SxProps<Theme> = {
  display: "flex",
};

const appBarStyles: SxProps<Theme> = (theme) => ({
  boxShadow: "none",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

const toolbarStyles: SxProps<Theme> = {};

const menuIconStyles: SxProps<Theme> = (theme) => ({
  marginLeft: theme.spacing(7.5),
});

const drawerStyles: SxProps<Theme> = (theme) => ({
  overflowX: "hidden",
  [`& .${drawerClasses.paper}`]: {
    overflowX: "hidden",
    position: "relative",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("xs")]: {
      width: drawerWidth,
    },
    [theme.breakpoints.up("md")]: {
      width: drawerWidth,
    },
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
});

const drawerClosedStyles: SxProps<Theme> = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up("xs")]: {
    width: smallDrawerWidth,
  },
  [theme.breakpoints.up("md")]: {
    width: theme.spacing(7),
  },
  [`& .${drawerClasses.paper}`]: {
    overflowX: "hidden",
    [theme.breakpoints.up("xs")]: {
      width: smallDrawerWidth,
    },
    [theme.breakpoints.up("md")]: {
      width: theme.spacing(7),
    },
  },
});

const drawerContainerStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "0 0.5rem",
  margin: "1.5rem",
};

const mediaStyles: SxProps<Theme> = (theme) => ({
  height: theme.spacing(4),
  width: theme.spacing(20),
});

const mainStyles: SxProps<Theme> = {
  flexGrow: 1,
  height: "100vh",
  overflow: "auto",
};

export const MenuComponent: Component = ({
  children,
  menuItems,
  permissions,
  logo,
  additionalContent,
}) => {
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);

  const handleSelect = (newSelected: string, parent?: string) =>
    setSelected(() => {
      if (parent) {
        return [parent, newSelected];
      }
      return [newSelected];
    });

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const menuContext: MenuContext = {
    permissions,
    open,
    selected,
    handleSelect,
  };

  return (
    <MenuContextProvider value={menuContext}>
      <Div sx={[rootStyles]} role="menu">
        <AppBar position="absolute" color="secondary" sx={[appBarStyles]}>
          <Toolbar sx={[toolbarStyles]}>
            <CardMedia sx={[mediaStyles]} image={logo} />
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={handleToggle}
              sx={[menuIconStyles]}
              data-testid="toggle-button"
              size="large"
            >
              <MenuIcon />
            </IconButton>
            <Box color="inherit" flexGrow={1}></Box>
            {additionalContent ? additionalContent : null}
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          sx={[drawerStyles, !open && drawerClosedStyles]}
          open={open}
        >
          <ToolbarDiv sx={[drawerContainerStyles]} />
          <Divider hidden />
          <MenuItems menuItems={menuItems} />
        </Drawer>
        <Main sx={[mainStyles]}>
          <ToolbarDiv />
          <Container maxWidth={false} disableGutters>
            {children}
          </Container>
        </Main>
      </Div>
    </MenuContextProvider>
  );
};
